import React, { useEffect, useMemo, useState } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import { useResizableColumns } from "./useResizableColumns";
import { SupportSortableHeader } from "../SupportSortableHeader";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE } from "../../components/TablePagination";
import "../../assets/css/DigixLeads.css";

const ISSUES_ROWS_PER_PAGE_KEY = "issuesRowsPerPage";

function formatDate(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB");
  } catch {
    return "—";
  }
}

function normalizeIssueRow(issue) {
  const assignedUsers = Array.isArray(issue.Assigned_Users)
    ? issue.Assigned_Users
    : Array.isArray(issue.assigned_users)
      ? issue.assigned_users
      : [];

  const assignedNames = assignedUsers
    .map((u) => u.User_Name ?? u.user_name ?? u.name)
    .filter(Boolean);

  return {
    id: issue.Issue_Id ?? issue.issue_id ?? issue.id,
    title: issue.Title ?? issue.title ?? "—",
    type: issue.Type ?? issue.type ?? "—",
    layer: issue.Issue_Layer ?? issue.issue_layer ?? "—",
    priority: issue.Priority ?? issue.priority ?? "—",
    status: issue.Status ?? issue.status ?? "—",
    taskTitle: issue.Task_Title ?? issue.task_title ?? issue.Task_Id ?? issue.task_id ?? "—",
    subTaskTitle: issue.Sub_Task_Title ?? issue.sub_task_title ?? issue.Sub_Task_Id ?? issue.sub_task_id ?? "—",
    dueDate: issue.Due_Date ?? issue.due_date ?? null,
    actualEndDate:
      issue.Actual_End_Date ??
      issue.actual_end_date ??
      issue.Updated_Date ??
      issue.updated_date ??
      issue.Updated_On ??
      issue.updated_on ??
      null,
    createdOn: issue.Created_On ?? issue.created_on ?? null,
    createdBy: issue.Created_By_Name ?? issue.created_by_name ?? issue.Created_By ?? issue.created_by ?? "—",
    updatedOn: issue.Updated_On ?? issue.updated_on ?? null,
    assignedUsers: assignedNames
  };
}

export default function IssuesTable({
  issues = [],
  onViewIssue,
  onEditIssue,
  onDeleteIssue,
  canEdit = true,
  canDelete = true
}) {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(ISSUES_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_ROWS_PER_PAGE;
  });
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const defaultWidths = [220, 110, 120, 110, 140, 170, 200, 110, 110, 130, 150, 130, 220, 120];
  const { getColProps, ResizeHandle } = useResizableColumns(defaultWidths);

  const rows = useMemo(() => (issues || []).map(normalizeIssueRow), [issues]);
  const getIssueSortValue = (issue, columnId) => {
    const dateValue = (v) => {
      if (!v) return 0;
      const ts = new Date(v).getTime();
      return Number.isFinite(ts) ? ts : 0;
    };
    switch (columnId) {
      case "title":
        return String(issue?.title ?? "").toLowerCase();
      case "type":
        return String(issue?.type ?? "").toLowerCase();
      case "layer":
        return String(issue?.layer ?? "").toLowerCase();
      case "priority":
        return String(issue?.priority ?? "").toLowerCase();
      case "status":
        return String(issue?.status ?? "").toLowerCase();
      case "task":
        return String(issue?.taskTitle ?? "").toLowerCase();
      case "subTask":
        return String(issue?.subTaskTitle ?? "").toLowerCase();
      case "dueDate":
        return dateValue(issue?.dueDate);
      case "actualEndDate":
        return dateValue(issue?.actualEndDate);
      case "createdOn":
        return dateValue(issue?.createdOn);
      case "createdBy":
        return String(issue?.createdBy ?? "").toLowerCase();
      case "updatedOn":
        return dateValue(issue?.updatedOn);
      case "assignees":
        return String((issue?.assignedUsers || []).join(", ")).toLowerCase();
      default:
        return "";
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortColumn) return rows;
    const mult = sortDirection === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
      const va = getIssueSortValue(a, sortColumn);
      const vb = getIssueSortValue(b, sortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return (Number(a?.id ?? 0) - Number(b?.id ?? 0)) * mult;
    });
  }, [rows, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * rowsPerPage;
  const current = sortedRows.slice(startIndex, startIndex + rowsPerPage);

  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(1);
    }
  }, [page, totalPages, sortedRows.length]);

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setPage(1);
    localStorage.setItem(ISSUES_ROWS_PER_PAGE_KEY, String(next));
  };

  const handleSort = (columnId) => {
    setPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  return (
    <div className="projects-container" style={{ padding: 0, backgroundColor: "transparent", minHeight: "auto" }}>
      <div className="table-wrapper" style={{ marginBottom: 16 }}>
        <div className="table-container">
          <table className="data-table projects-table-frozen" style={{ minWidth: 1280 }}>
            <thead>
              <tr>
                <th className="col-sticky col-0 projects-th-sortable" {...getColProps(0)}><div className="projects-th-sort-row"><SupportSortableHeader columnId="title" label="Title" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={0} /></th>
                <th className="projects-th-sortable" {...getColProps(1)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="type" label="Type" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={1} /></th>
                <th className="projects-th-sortable" {...getColProps(2)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="layer" label="Layer" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={2} /></th>
                <th className="projects-th-sortable" {...getColProps(3)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="priority" label="Priority" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={3} /></th>
                <th className="projects-th-sortable" {...getColProps(4)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="status" label="Status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={4} /></th>
                <th className="projects-th-sortable" {...getColProps(5)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="task" label="Task" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={5} /></th>
                <th className="projects-th-sortable" {...getColProps(6)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="subTask" label="Sub task" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={6} /></th>
                <th className="projects-th-sortable" {...getColProps(7)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="dueDate" label="Due date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={7} /></th>
                <th className="projects-th-sortable" {...getColProps(8)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="actualEndDate" label="Actual end date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={8} /></th>
                <th className="projects-th-sortable" {...getColProps(9)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="createdOn" label="Created on" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={9} /></th>
                <th className="projects-th-sortable" {...getColProps(10)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="createdBy" label="Created by" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={10} /></th>
                <th className="projects-th-sortable" {...getColProps(11)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="updatedOn" label="Updated on" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={11} /></th>
                <th className="projects-th-sortable" {...getColProps(12)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="assignees" label="Assignees" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={12} /></th>
                {(onEditIssue || onDeleteIssue) ? (
                  <th className="col-sticky col-action" {...getColProps(13)}>Actions</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
                <tr>
                  <td colSpan={(onEditIssue || onDeleteIssue) ? 14 : 13} style={{ textAlign: "center", padding: "24px", color: "#5E6C84" }}>
                    No issues yet.
                  </td>
                </tr>
              ) : (
                current.map((r) => (
                  <tr key={r.id ?? r.title}>
                    <td className="col-sticky col-0" style={{ textAlign: "left" }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: onViewIssue ? "#2977ed" : "#172B4D",
                          cursor: onViewIssue ? "pointer" : "default"
                        }}
                        role={onViewIssue ? "button" : undefined}
                        tabIndex={onViewIssue ? 0 : undefined}
                        onClick={() => {
                          if (onViewIssue && r.id != null) onViewIssue(r.id);
                        }}
                        onKeyDown={(e) => {
                          if (!onViewIssue) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            if (r.id != null) onViewIssue(r.id);
                          }
                        }}
                      >
                        {r.title}
                      </div>
                    </td>
                    <td>{r.type}</td>
                    <td>{r.layer}</td>
                    <td>
                      <span className={`status-badge status-${String(r.priority || "not-set").toLowerCase().replace(/\s/g, "-")}`}>
                        {r.priority}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${String(r.status || "not-set").toLowerCase().replace(/\s/g, "-")}`}>
                        {r.status}
                      </span>
                    </td>
                    <td style={{ textAlign: "left" }}>{r.taskTitle}</td>
                    <td style={{ textAlign: "left" }}>{r.subTaskTitle}</td>
                    <td>{formatDate(r.dueDate)}</td>
                    <td>{formatDate(r.actualEndDate)}</td>
                    <td>{formatDate(r.createdOn)}</td>
                    <td style={{ textAlign: "left" }}>{r.createdBy}</td>
                    <td>{formatDate(r.updatedOn)}</td>
                    <td style={{ textAlign: "left" }}>
                      {r.assignedUsers.length ? r.assignedUsers.join(", ") : "—"}
                    </td>
                    {(onEditIssue || onDeleteIssue) ? (
                      <td className="col-sticky col-action">
                        <div className="project-actions-cell">
                          {onEditIssue && canEdit ? (
                            <button
                              type="button"
                              className="project-action-btn project-action-btn-icon project-action-btn-edit"
                              title="Edit issue"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (r.id != null) onEditIssue(r.id);
                              }}
                            >
                              <FaEdit />
                            </button>
                          ) : null}
                          {onDeleteIssue && canDelete ? (
                            <button
                              type="button"
                              className="project-action-btn project-action-btn-icon project-action-btn-deactivate"
                              title="Delete issue"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (r.id != null) onDeleteIssue(r.id);
                              }}
                            >
                              <FaTrash />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {sortedRows.length > 0 && (
        <TablePagination
          currentPage={safePage}
          totalItems={sortedRows.length}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          itemLabel="issues"
          rowsPerPageSelectId="issues-rows-per-page"
        />
      )}
    </div>
  );
}

